import { css, html, LitElement } from "lit";
import { type PixelIconName, renderPixelIcon } from "../shared/pixel-icons";
import { windowsMePrimitives } from "../shared/windows-me-styles";

export type DesktopShortcutOpenSource = "keyboard" | "double-click" | "touch";

export class DesktopShortcut extends LitElement {
	static properties = {
		description: { type: String },
		disabled: { type: Boolean, reflect: true },
		icon: { type: String },
		label: { type: String },
		selected: { type: Boolean, reflect: true },
		shortcutId: { type: String, attribute: "shortcut-id" },
	};

	static styles = [
		windowsMePrimitives,
		css`
			:host {
				display: block;
				inline-size: 76px;
				touch-action: manipulation;
				user-select: none;
			}

			button {
				display: grid;
				inline-size: 100%;
				min-block-size: 86px;
				padding: 6px 4px;
				gap: 5px;
				justify-items: center;
				align-content: start;
				color: #ffffff;
				background: transparent;
				border: 0;
				cursor: default;
				font: 400 11px/1.1 var(--shell-font);
			}

			button:hover .label {
				background: color-mix(in srgb, var(--shell-selection) 28%, transparent);
			}

			button:focus-visible {
				outline: none;
			}

			button:focus-visible .label,
			:host([selected]) .label {
				background: var(--shell-selection);
				color: var(--shell-selection-text);
			}

			button:active .label {
				background: color-mix(in srgb, var(--shell-selection) 78%, black 8%);
				color: var(--shell-selection-text);
			}

			.icon-wrap {
				inline-size: 42px;
				block-size: 42px;
				display: grid;
				place-items: center;
				filter: drop-shadow(1px 1px 0 rgba(0, 0, 0, 0.45));
			}

			.pixel-icon {
				inline-size: 32px;
				block-size: 32px;
			}

			.label {
				max-inline-size: 100%;
				padding: 1px 3px 2px;
				border: 1px dotted transparent;
				text-align: center;
				text-shadow: 1px 1px 0 rgba(0, 0, 0, 0.52);
				word-break: break-word;
			}

			button:focus-visible .label {
				border-color: var(--shell-selection-text);
			}

			:host([disabled]) {
				opacity: 0.56;
			}

			:host([disabled]) button {
				cursor: not-allowed;
			}
		`,
	];

	description = "";

	disabled = false;

	icon: PixelIconName = "radio";

	label = "";

	selected = false;

	shortcutId = "";

	render() {
		const label = this.label || "Desktop item";
		return html`
			<button
				class="focus-ring"
				type="button"
				aria-describedby=${this.description ? "shortcut-description" : ""}
				aria-pressed=${this.selected ? "true" : "false"}
				aria-label=${this.disabled ? `${label}, unavailable` : label}
				?disabled=${this.disabled}
				@click=${this.#handleClick}
				@dblclick=${this.#handleDoubleClick}
				@keydown=${this.#handleKeyDown}
				@pointerup=${this.#handlePointerUp}
			>
				<span class="icon-wrap" aria-hidden="true">
					${renderPixelIcon(this.icon, "")}
				</span>
				<span class="label">${label}</span>
			</button>
			${
				this.description
					? html`<span id="shortcut-description" class="visually-hidden">
						${this.description}
					</span>`
					: null
			}
		`;
	}

	#handleClick() {
		this.#emit("desktop-shortcut-select");
	}

	#handleDoubleClick() {
		if (this.disabled) return;
		this.#emit("desktop-shortcut-open", {
			source: "double-click" satisfies DesktopShortcutOpenSource,
		});
	}

	#handleKeyDown(event: KeyboardEvent) {
		if (this.disabled) return;
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			this.#emit("desktop-shortcut-open", {
				source: "keyboard" satisfies DesktopShortcutOpenSource,
			});
		}
		if (event.key === "Escape") {
			this.dispatchEvent(
				new CustomEvent("desktop-shortcut-clear", {
					bubbles: true,
					composed: true,
					detail: { shortcutId: this.shortcutId },
				}),
			);
		}
	}

	#handlePointerUp(event: PointerEvent) {
		if (this.disabled || event.pointerType !== "touch") return;
		this.#emit("desktop-shortcut-open", {
			source: "touch" satisfies DesktopShortcutOpenSource,
		});
	}

	#emit(name: string, detail: Record<string, unknown> = {}) {
		this.dispatchEvent(
			new CustomEvent(name, {
				bubbles: true,
				composed: true,
				detail: {
					icon: this.icon,
					label: this.label,
					shortcutId: this.shortcutId,
					...detail,
				},
			}),
		);
	}
}

if (!customElements.get("desktop-shortcut")) {
	customElements.define("desktop-shortcut", DesktopShortcut);
}
